import styled from 'styled-components';
import { P, fs, radius, ease } from '../../theme/tokens';
import { Card } from '../../components';

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;

  button {
    height: 40px;
  }
`;

export const PipelineCard = styled(Card)`
  padding: 16px 18px 14px;
  margin-bottom: 20px;

  > header {
    justify-content: space-between;
    margin-bottom: 12px;
  }
  > header > div {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }
  > header > span {
    font-size: ${fs.xs}px;
  }
`;

const rowBox = `
  border: 1px solid ${P.line};
  border-radius: ${radius.md}px;
`;

export const LinkRow = styled.button`
  ${rowBox}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 9px 11px;
  background: ${P.white};
  font-family: inherit;
  font-size: ${fs.sm}px;
  font-weight: 600;
  color: ${P.ink};
  text-align: left;
  cursor: pointer;
  transition: background 150ms ${ease};

  &:hover {
    background: ${P.rowHover};
  }
  svg {
    flex: none;
    font-size: 10px;
    color: ${P.subtle};
  }
`;

export const ItemCard = styled.li`
  ${rowBox}
  padding: 10px 11px;

  strong {
    display: block;
    font-size: ${fs.sm}px;
    font-weight: 600;
    color: ${P.ink};
  }
  small {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: ${P.subtle};
  }
  p {
    margin: 5px 0 0;
    font-size: ${fs.xs}px;
    color: ${P.muted};
  }
`;

export const FileRow = styled.li`
  ${rowBox}
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 11px;

  > svg {
    font-size: 12px;
    color: ${P.subtle};
  }
  div {
    flex: 1;
    min-width: 0;
  }
  strong {
    display: block;
    font-size: ${fs.xs}px;
    font-weight: 600;
    color: ${P.ink};
    overflow-wrap: anywhere;
  }
  small {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: ${P.subtle};
  }
`;

export const CallHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  button {
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: ${P.navy};
    white-space: nowrap;
    cursor: pointer;
  }
  button svg {
    margin-right: 4px;
    font-size: 8px;
  }
`;

export const Why = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: ${fs.sm}px;
    color: ${P.muted};
    text-wrap: pretty;
  }
  svg {
    margin-top: 3px;
    font-size: 11px;
    color: ${P.success};
  }
`;

export const NextStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;

  h3 {
    margin: 0;
  }
  p {
    margin: 0;
    font-size: ${fs.sm}px;
    font-weight: 600;
    color: ${P.ink};
    text-wrap: pretty;
  }
  button {
    justify-content: center;
    width: 100%;
    height: 38px;
  }
  button svg {
    font-size: 11px;
  }
`;

export const EditActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

export const Dropzone = styled.label`
  position: relative;
  display: block;
  margin-top: 4px;
  padding: 16px;
  border: 1px dashed ${P.border};
  border-radius: ${radius.md}px;
  text-align: center;
  cursor: pointer;

  &:focus-within,
  &[data-over='true'] {
    border-color: ${P.navy};
  }
  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }
  > svg {
    font-size: 13px;
    color: ${P.subtle};
  }
  > span {
    display: block;
    margin-top: 6px;
    font-size: ${fs.xs}px;
    color: ${P.subtle};
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  margin-top: 10px;
`;

export const Chip = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    height: 34px;
    padding: 0 16px;
    border: 1px solid ${(p) => (p.$active ? 'transparent' : P.border)};
    border-radius: ${radius.pill}px;
    background: ${(p) => (p.$active ? P.grad : P.white)};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => (p.$active ? P.white : P.muted)};
    cursor: pointer;
  }
  &&:hover {
    color: ${(p) => (p.$active ? P.white : P.navyInk)};
    border-color: ${(p) => (p.$active ? 'transparent' : P.navy)};
  }
`;

export const PropRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 13px 20px;
  border: none;
  border-bottom: 1px solid ${P.line};
  background: ${P.white};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${P.rowHover};
  }
  > div {
    min-width: 0;
  }
  strong {
    display: block;
    font-size: ${fs.sm}px;
    font-weight: 600;
    color: ${P.ink};
  }
  small {
    display: block;
    margin-top: 3px;
    font-size: 11px;
    color: ${P.subtle};
  }
  > span {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 10px;
  }
  > span svg {
    font-size: 10px;
    color: ${P.subtle};
  }
`;
